/**
 * Web Push Notifications — Iter152
 *
 * Helpers for registering the service worker, asking for permission,
 * subscribing to PushManager with the backend-provided VAPID public key,
 * and POSTing the subscription to the backend so the server can deliver
 * push messages from background jobs.
 *
 * All functions return clear `{ok, reason?, ...}` shapes — callers can
 * surface them to the user without parsing exceptions.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SW_URL = "/sw.js";

/**
 * Browser support gate. Push requires SW + PushManager + Notification + a
 * secure context (HTTPS or localhost).
 */
export function isPushSupported() {
  if (typeof window === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (!("Notification" in window)) return false;
  if (!window.isSecureContext) return false;
  return true;
}

export function currentPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

/** Register `/sw.js` once and return the active registration. */
export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_URL);
  } catch (e) {
    console.warn("[push] SW registration failed:", e);
    return null;
  }
}

/** VAPID public keys are URL-safe base64; PushManager needs Uint8Array. */
function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function fetchVapidPublicKey() {
  const r = await fetch(`${API}/push/vapid-public-key`);
  if (!r.ok) throw new Error(`vapid key fetch failed (${r.status})`);
  const data = await r.json();
  return data.public_key;
}

/** Ask permission, subscribe, POST to backend. Returns {ok, subscription?}. */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }

  if (Notification.permission === "denied") {
    return { ok: false, reason: "denied" };
  }
  if (Notification.permission === "default") {
    const res = await Notification.requestPermission();
    if (res !== "granted") return { ok: false, reason: res };
  }

  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: "sw_failed" };

  // Wait for an active worker so `pushManager` is ready.
  if (!reg.active) {
    await navigator.serviceWorker.ready;
  }

  let publicKey;
  try {
    publicKey = await fetchVapidPublicKey();
  } catch (e) {
    return { ok: false, reason: "vapid_unavailable" };
  }

  let subscription = await reg.pushManager.getSubscription();
  if (!subscription) {
    try {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    } catch (e) {
      console.warn("[push] subscribe failed:", e);
      return { ok: false, reason: "subscribe_failed", error: String(e) };
    }
  }

  const sub = subscription.toJSON();
  const r = await fetch(`${API}/push/subscribe`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: sub.keys,
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
    }),
  });
  if (!r.ok) return { ok: false, reason: "backend_save_failed", status: r.status };

  return { ok: true, subscription: sub };
}

/** Remove the subscription locally + on backend. */
export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  if (!reg) return { ok: true, reason: "no_registration" };
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true, reason: "not_subscribed" };

  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch (e) {
    console.warn("[push] local unsubscribe failed:", e);
  }
  try {
    await fetch(`${API}/push/unsubscribe`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ endpoint }),
    });
  } catch (e) {
    console.warn("[push] backend unsubscribe failed:", e);
  }
  return { ok: true };
}

/** Returns the current subscription state — used by the UI toggle. */
export async function getPushStatus() {
  if (!isPushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return {
    supported: true,
    permission,
    subscribed: !!sub,
    endpoint: sub?.endpoint || null,
  };
}

/** Fire a backend-driven test push to confirm e2e delivery. */
export async function fireTestPush() {
  const r = await fetch(`${API}/push/test`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({}),
  });
  if (!r.ok) return { ok: false, status: r.status };
  return { ok: true, result: await r.json() };
}
